import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaTrash, FaImage, FaBell, FaUpload, FaPlus, FaTimes, FaThLarge } from 'react-icons/fa';
import './AddProduct.css';

const AddProduct = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [categoryLabels, setCategoryLabels] = useState({});
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [product, setProduct] = useState({
    name: '',
    description: '',
    price: '',
    sale_price: '',
    category: 'electronics',
    stock: '',
    status: 'active',
    low_stock_threshold: '5',
    images: [],
    featured: 0,
    is_new_arrival: 0,
    is_on_sale: 0,
    slider_badge: '',
    product_details_title: 'Product Details',
    product_details: '',
    fabric_care_title: 'Fabric & Care',
    fabric_care: '',
    delivery_info_title: 'Delivery Information',
    delivery_info: '',
    return_policy_title: 'Return & Exchange Policy',
    return_policy: '',
  });

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const response = await fetch('https://demo-site.creativepulse.pk/demo-api/public/get-settings.php');
      const data = await response.json();

      if (data.success && data.settings) {
        let categoriesArray = [];
        let labelsObj = {};

        if (data.settings.categories) {
          try { categoriesArray = JSON.parse(data.settings.categories); } catch (e) { console.error(e); }
        }
        if (data.settings.category_labels) {
          try { labelsObj = JSON.parse(data.settings.category_labels); } catch (e) { console.error(e); }
        }

        setCategories(categoriesArray);
        setCategoryLabels(labelsObj);
      } else {
        setCategories(['electronics', 'clothing', 'books', 'accessories', 'home', 'sports']);
      }
    } catch (error) {
      console.error('Error loading categories:', error);
      setCategories(['electronics', 'clothing', 'books', 'accessories', 'home', 'sports']);
    }
  };

  const [guide, setGuide] = useState({
    guide_title: 'Size Guide',
    guide_description: '',
    guide_rows: [],
    guide_image: '',
    show_on_page: 1,
  });
  const [uploadingGuideImage, setUploadingGuideImage] = useState(false);

  const addGuideRow = () => {
    setGuide(prev => ({ ...prev, guide_rows: [...prev.guide_rows, { size: '' }] }));
  };

  const updateGuideRow = (index, field, value) => {
    setGuide(prev => {
      const rows = [...prev.guide_rows];
      rows[index] = { ...rows[index], [field]: value };
      return { ...prev, guide_rows: rows };
    });
  };

  const removeGuideRow = (index) => {
    setGuide(prev => ({ ...prev, guide_rows: prev.guide_rows.filter((_, i) => i !== index) }));
  };

  const handleGuideImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('image', file);
    setUploadingGuideImage(true);
    try {
      const response = await fetch('https://demo-site.creativepulse.pk/demo-api/admin/upload-guide-image.php', {
        method: 'POST',
        body: formData
      });
      const data = await response.json();
      if (data.success) {
        setGuide(prev => ({ ...prev, guide_image: data.image_url }));
        toast.success('Guide image uploaded!');
      } else {
        toast.error(data.message || 'Upload failed');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setUploadingGuideImage(false);
      e.target.value = '';
    }
  };

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? (e.target.checked ? 1 : 0) : e.target.value;
    setProduct({ ...product, [e.target.name]: value });
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (product.images.length + files.length > 5) {
      toast.error('Maximum 5 images allowed');
      return;
    }

    const formData = new FormData();
    files.forEach(file => formData.append('images[]', file));
    setUploading(true);

    try {
      const response = await fetch('https://demo-site.creativepulse.pk/demo-api/admin/upload-product-images.php', {
        method: 'POST',
        body: formData
      });
      const data = await response.json();
      if (data.success) {
        setProduct({ ...product, images: [...product.images, ...data.images] });
        toast.success(`${data.images.length} images uploaded successfully`);
      } else {
        toast.error(data.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Connection error');
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (index) => {
    const newImages = [...product.images];
    newImages.splice(index, 1);
    setProduct({ ...product, images: newImages });
  };

  const isStockLow = () => {
    const currentStock = parseInt(product.stock);
    const threshold = parseInt(product.low_stock_threshold);
    return !isNaN(currentStock) && !isNaN(threshold) && currentStock <= threshold && currentStock > 0;
  };

  const isOutOfStock = () => {
    const currentStock = parseInt(product.stock);
    return !isNaN(currentStock) && currentStock === 0;
  };

  const handleClose = () => navigate('/admin/products');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const lowStockThreshold = parseInt(product.low_stock_threshold);
    if (isNaN(lowStockThreshold) || lowStockThreshold < 0) {
      toast.error('Low stock threshold must be a valid positive number');
      setSubmitting(false);
      return;
    }

    if (product.images.length === 0) {
      toast.error('Please add at least one image');
      setSubmitting(false);
      return;
    }

    const submitData = {
      name: product.name,
      description: product.description,
      price: product.price,
      sale_price: product.sale_price,
      category: product.category,
      stock: product.stock,
      status: product.status,
      low_stock_threshold: product.low_stock_threshold,
      image: product.images[0],
      images: JSON.stringify(product.images),
      featured: product.featured,
      is_new_arrival: product.is_new_arrival,
      is_on_sale: product.is_on_sale,
      slider_badge: product.slider_badge,
      product_details_title: product.product_details_title,
      product_details: product.product_details,
      fabric_care_title: product.fabric_care_title,
      fabric_care: product.fabric_care,
      delivery_info_title: product.delivery_info_title,
      delivery_info: product.delivery_info,
      return_policy_title: product.return_policy_title,
      return_policy: product.return_policy,
    };

    try {
      const response = await fetch('https://demo-site.creativepulse.pk/demo-api/admin/add-product.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submitData)
      });
      const data = await response.json();

      if (data.success) {
        // Save the Size Guide now that we have a real product_id — only if
        // the admin actually entered something, so we don't create an empty
        // guide row for every new product by default.
        const hasGuideContent = guide.guide_description.trim() || guide.guide_rows.length > 0 || guide.guide_image;
        if (hasGuideContent) {
          try {
            await fetch('https://demo-site.creativepulse.pk/demo-api/admin/save-product-guide.php', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ product_id: data.product_id, ...guide })
            });
          } catch (guideError) {
            console.error('Error saving guide:', guideError);
            // Don't block navigation on this — the product itself saved fine.
          }
        }

        toast.success('Product added successfully!');
        setTimeout(() => navigate(`/admin/edit-product/${data.product_id}`), 1200);
      } else {
        toast.error(data.message || 'Failed to add product');
      }
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('Connection error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="add-product-overlay">
      <div className="add-product-backdrop" onClick={handleClose}></div>

      <div className="add-product-drawer">
        <div className="drawer-header">
          <h2>Add Product</h2>
          <button type="button" className="drawer-close-btn" onClick={handleClose}>
            <FaTimes />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="drawer-form">
          <div className="drawer-body">

            <section className="drawer-section">
              <h3 className="drawer-section-title">Basic Details</h3>

              <div className="form-group">
                <label>Product Name</label>
                <input type="text" name="name" value={product.name} onChange={handleChange} required />
              </div>

              <div className="form-group">
                <label>Category</label>
                <select name="category" value={product.category} onChange={handleChange} required>
                  <option value="">Select Category</option>
                  {categories.map(cat => (
                    <option key={cat} value={cat}>
                      {categoryLabels[cat] || cat.charAt(0).toUpperCase() + cat.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Product Description</label>
                <textarea name="description" value={product.description} onChange={handleChange} rows="4" required />
              </div>
            </section>

            <section className="drawer-section">
              <div className="drawer-row">
                <div className="form-group">
                  <label>Price (PKR)</label>
                  <input type="number" name="price" value={product.price} onChange={handleChange} step="0.01" required />
                </div>
                <div className="form-group">
                  <label>Sale Price (PKR)</label>
                  <input type="number" name="sale_price" value={product.sale_price} onChange={handleChange} placeholder="Optional" />
                </div>
              </div>

              <div className="drawer-row">
                <div className="form-group">
                  <label>Stock Quantity</label>
                  <input type="number" name="stock" value={product.stock} onChange={handleChange} required min="0" />
                </div>
                <div className="form-group">
                  <label>Product Status</label>
                  <select name="status" value={product.status} onChange={handleChange}>
                    <option value="active">Active</option>
                    <option value="draft">Draft</option>
                  </select>
                </div>
              </div>

              {isStockLow() && (
                <div className="stock-warning">
                  <FaBell className="warning-icon" />
                  <span>Low stock! Current stock is below threshold</span>
                </div>
              )}
              {isOutOfStock() && (
                <div className="stock-out">
                  <FaBell className="warning-icon" />
                  <span>Out of stock!</span>
                </div>
              )}

              <div className="form-group">
                <label>Low Stock Threshold</label>
                <input type="number" name="low_stock_threshold" value={product.low_stock_threshold} onChange={handleChange} required min="0" />
                <small>Alert when stock falls below this number</small>
              </div>
            </section>

            <section className="drawer-section homepage-display-section">
              <h3 className="drawer-section-title with-icon"><FaThLarge /> Homepage &amp; Store Display</h3>
              <p className="section-sub">Control how this product appears on the homepage and default system pages.</p>

              <label className="field-label">Product Tags</label>
              <div className="tag-toggle-row">
                <button
                  type="button"
                  className={`tag-toggle ${Number(product.is_new_arrival) === 1 ? 'active' : ''}`}
                  onClick={() => setProduct({ ...product, is_new_arrival: Number(product.is_new_arrival) === 1 ? 0 : 1 })}
                >
                  {Number(product.is_new_arrival) === 1 && <span className="tag-check">✓</span>} New Arrival
                </button>
                <button
                  type="button"
                  className={`tag-toggle ${Number(product.is_on_sale) === 1 ? 'active' : ''}`}
                  onClick={() => {
                    // Only block turning it ON — always allow turning it back off, even
                    // if sale_price is somehow empty by then.
                    if (Number(product.is_on_sale) !== 1 && (!product.sale_price || product.sale_price === '')) {
                      toast.error('Please add a Sale Price before marking this product as Sale.');
                      return;
                    }
                    setProduct({ ...product, is_on_sale: Number(product.is_on_sale) === 1 ? 0 : 1 });
                  }}
                >
                  {Number(product.is_on_sale) === 1 && <span className="tag-check">✓</span>} Sale
                </button>
              </div>
              <p className="tags-note">Products tagged as New Arrival or Sale will appear on the default New Arrivals or Sale pages.</p>

              <div className="slider-toggle-row">
                <div>
                  <strong>Show in Homepage Product Slider</strong>
                  <p>Turn this on if you want this product to appear in the homepage product slider.</p>
                </div>
                <label className="toggle-switch">
                  <input type="checkbox" name="featured" checked={product.featured === 1} onChange={handleChange} />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              <div className="form-group">
                <label>Slider Badge</label>
                <select name="slider_badge" value={product.slider_badge} onChange={handleChange}>
                  <option value="">No Badge</option>
                  <option value="New Arrival">New Arrival</option>
                  <option value="Best Seller">Best Seller</option>
                  <option value="Sale">Sale</option>
                  <option value="Limited">Limited</option>
                </select>
                <small className="ss-hint">Only shown if "Show in Homepage Product Slider" is turned on.</small>
              </div>
            </section>

            <div className="drawer-section">
              <h3 className="drawer-section-title">Product Information</h3>

              <div className="content-block">
                <div className="form-group">
                  <label>Section 1 — Header</label>
                  <input type="text" name="product_details_title" value={product.product_details_title} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Section 1 — Description</label>
                  <textarea rows="4" name="product_details" value={product.product_details} onChange={handleChange}
                    placeholder="Regular fit&#10;Premium fabric blend" />
                </div>
              </div>

              <div className="content-block">
                <div className="form-group">
                  <label>Section 2 — Header</label>
                  <input type="text" name="fabric_care_title" value={product.fabric_care_title} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Section 2 — Description</label>
                  <textarea rows="4" name="fabric_care" value={product.fabric_care} onChange={handleChange}
                    placeholder="Cotton blend fabric&#10;Machine wash cold" />
                </div>
              </div>

              <div className="content-block">
                <div className="form-group">
                  <label>Section 3 — Header</label>
                  <input type="text" name="delivery_info_title" value={product.delivery_info_title} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Section 3 — Description</label>
                  <textarea rows="3" name="delivery_info" value={product.delivery_info} onChange={handleChange}
                    placeholder="Standard delivery within 3–5 working days" />
                </div>
              </div>

              <div className="content-block">
                <div className="form-group">
                  <label>Section 4 — Header</label>
                  <input type="text" name="return_policy_title" value={product.return_policy_title} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Section 4 — Description</label>
                  <textarea rows="3" name="return_policy" value={product.return_policy} onChange={handleChange}
                    placeholder="Exchange available within 7 days" />
                </div>
              </div>
            </div>

            <section className="drawer-section homepage-display-section">
              <h3 className="drawer-section-title with-icon">📏 Size Guide / Product Guide</h3>
              <div className="variants-section">
                <p className="help-text">Add helpful sizing or buying guidance so customers can choose the right product. This saves automatically when you add the product.</p>

                <div className="form-group">
                  <label>Guide Title</label>
                  <input
                    type="text"
                    value={guide.guide_title}
                    onChange={(e) => setGuide({ ...guide, guide_title: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Guide Description</label>
                  <textarea
                    rows="3"
                    value={guide.guide_description}
                    onChange={(e) => setGuide({ ...guide, guide_description: e.target.value })}
                    placeholder="Use this guide to choose the right size. Measurements may vary slightly depending on fabric and fit."
                  />
                </div>
                
                {/* 
                <label className="field-label">Available Sizes</label>
                {guide.guide_rows.map((row, index) => (
                  <div key={index} className="drawer-row guide-row-single">
                    <input
                      type="text"
                      placeholder="e.g. S, M, L, XL"
                      value={row.size}
                      onChange={(e) => updateGuideRow(index, 'size', e.target.value)}
                    />
                    <button type="button" className="remove-variant-btn" onClick={() => removeGuideRow(index)}><FaTrash /></button>
                  </div>
                ))}
                <button type="button" className="add-variant-btn" onClick={addGuideRow}>
                  <FaPlus /> Add Row
                </button>
                */}
<label className="field-label">Available Sizes</label>
<div className="sizes-grid">
  {guide.guide_rows.map((row, index) => (
    <div key={index} className="size-box">
      <input
        type="text"
        className="size-input"
        placeholder="S"
        value={row.size}
        onChange={(e) => updateGuideRow(index, 'size', e.target.value)}
      />
      <button
        type="button"
        className="size-remove-btn"
        onClick={() => removeGuideRow(index)}
        aria-label="Remove size"
      >
        <FaTrash />
      </button>
    </div>
  ))}
  <button type="button" className="add-size-box" onClick={addGuideRow}>
    Add More<br />Size
  </button>
</div>                

                <div className="form-group" style={{ marginTop: '14px' }}>
                  <label>Size Guide Image</label>
                  {guide.guide_image && (
                    <div className="variant-image-preview">
                      <img src={guide.guide_image} alt="Size guide" />
                      <button type="button" className="remove-variant-img" onClick={() => setGuide({ ...guide, guide_image: '' })}>✕</button>
                    </div>
                  )}
                  <input
                    type="file"
                    id="guideImageUploadAdd"
                    accept="image/*"
                    onChange={handleGuideImageUpload}
                    style={{ display: 'none' }}
                  />
                  <button
                    type="button"
                    className="upload-variant-btn"
                    onClick={() => document.getElementById('guideImageUploadAdd').click()}
                    disabled={uploadingGuideImage}
                  >
                    <FaUpload /> {uploadingGuideImage ? 'Uploading...' : 'Upload Size Guide Image'}
                  </button>
                </div>

                <div className="slider-toggle-row">
                  <div>
                    <strong>Show Guide on Product Page</strong>
                    <p>Turn this on to show the guide on the product detail page.</p>
                  </div>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={guide.show_on_page === 1}
                      onChange={(e) => setGuide({ ...guide, show_on_page: e.target.checked ? 1 : 0 })}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              </div>
            </section>

            <section className="drawer-section">
              <label className="field-label">Product Images (Max 5)</label>
              <div className="multi-image-upload">
                <div className="image-preview-grid">
                  {product.images.map((img, index) => (
                    <div key={index} className="image-preview-item">
                      <img src={img} alt={`Preview ${index}`} />
                      <button type="button" className="remove-image-btn" onClick={() => removeImage(index)}>
                        <FaTrash />
                      </button>
                    </div>
                  ))}
                  {product.images.length < 5 && (
                    <div className="image-upload-box">
                      <input
                        type="file"
                        id="productImages"
                        accept="image/*"
                        multiple
                        onChange={handleImageUpload}
                        style={{ display: 'none' }}
                        disabled={uploading}
                      />
                      <button
                        type="button"
                        className="upload-images-btn"
                        onClick={() => document.getElementById('productImages').click()}
                        disabled={uploading}
                      >
                        <FaImage /> {uploading ? 'Uploading...' : 'Upload Images'}
                      </button>
                    </div>
                  )}
                </div>
                <p className="help-text">Upload up to 5 images. First image will be the main product image.</p>
              </div>
            </section>
          </div>

          <div className="drawer-footer">
            <button type="button" onClick={handleClose} className="cancel-btn">Cancel</button>
            <button type="button" className="draft-btn" disabled title="Not wired up yet">Save as Draft</button>
            <button type="submit" disabled={submitting || product.images.length === 0} className="submit-btn">
              {submitting ? 'Adding...' : 'Add Product'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddProduct;